import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CategoryIcon from '@mui/icons-material/Category';
import GetAppIcon from '@mui/icons-material/GetApp';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import {
  getSecurityEndpointDetailsHref,
  getSecurityNavItemPath,
  getSecurityThreatHuntingDetailHref,
  SECURITY_ENDPOINTS_HREF,
} from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type {
  WazuhThreatHuntingEndpointItem,
  WazuhThreatHuntingFindingItem,
} from 'src/types/mindtime/wazuhThreatHunting';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrGlobalScopeFilters,
  EdrPageHeader,
  SubscriptionUnavailableMessage,
  type EdrToolbarFilter,
  formatEdrNumber,
}  from '../shared/edrPrimitives';
import {
  getThreatHuntingAffectedEndpointListColumns,
  getThreatHuntingFindingId,
  getThreatHuntingFindingListColumns,
  mapThreatHuntingFacetOptions,
  THREAT_HUNTING_SEVERITY_FILTER_OPTIONS,
  ThreatSummaryKpiGrid,
} from './threatHuntingDetailModel';
import {
  useThreatHuntingData,
  useThreatHuntingExport,
  type ThreatHuntingSortParams,
} from './useThreatHuntingData';
import { useThreatHuntingUserFilterOptions } from './useThreatHuntingUserFilterOptions';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Threat Hunting', to: getSecurityNavItemPath('threat-hunting') ?? '' },
  ],
};


const ThreatHunting: FC = () => {
  const navigate = useNavigate();
  const { setDateRange, setSelectedEndpointId, selectedClientId } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.threatHunting);
  const {
    severityBand = '',
    user = '',
    mitreId = '',
    eventType = '',
  } = filters;
  const organizationId = selectedClientId ?? '';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [endpointsSortParams, setEndpointsSortParams] = useState<ThreatHuntingSortParams | null>(
    null
  );
  const [resetEndpointsSortFlag, setResetEndpointsSortFlag] = useState(false);
  const { canWidget, permissionsResolved } = useSubscriptionModulePermissions(
    EDR_SOLUTION_ID,
    'threat-hunting',
  );
  const canSummary = canWidget('summary');
  const canResultsList = canWidget('resultsList');
  const canAffectedEndpoints = canWidget('affectedEndpoints');
  const showUnavailable =
    permissionsResolved && !canSummary && !canResultsList && !canAffectedEndpoints;

  const huntingFilters = useMemo(
    () => ({
      user: filters.user,
      mitreId: filters.mitreId,
      eventType: filters.eventType,
      severityBand: filters.severityBand,
      organizationId,
    }),
    [filters.user, filters.mitreId, filters.eventType, filters.severityBand, organizationId]
  );

  const { loading, summary, items: paged, endpoints: endpointsPaged, facets } = useThreatHuntingData(
    page,
    rowsPerPage,
    search,
    huntingFilters,
    endpointsSortParams
  );
  const { exporting, handleExport } = useThreatHuntingExport(search, huntingFilters);
  const {
    options: userFilterOptions,
    loading: userFilterLoading,
    onSearch: onUserFilterSearch,
  } = useThreatHuntingUserFilterOptions({
    pageOrganizationId: organizationId || undefined,
  });

  const openFindingDetail = useCallback(
    (item: WazuhThreatHuntingFindingItem) => {
      const id = getThreatHuntingFindingId(item);
      if (!id) return;
      navigate(getSecurityThreatHuntingDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  const openEndpointDetails = useCallback(
    (item: WazuhThreatHuntingEndpointItem) => {
      const systemWazuhAgentId = item.systemWazuhAgentId?.trim();
      if (!systemWazuhAgentId) return;
      navigate(getSecurityEndpointDetailsHref(systemWazuhAgentId), {
        state: { endpointName: item.endpoint ?? item.name },
      });
    },
    [navigate]
  );

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;
  const affectedEndpoints = endpointsPaged?.items ?? [];
  const affectedEndpointsTotal = endpointsPaged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(
    resetFilters,
    setDateRange,
    setPage,
    setSelectedEndpointId
  );

  const handleEndpointsSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc' | null) => {
      setEndpointsSortParams(direction ? { column: columnId, sort: direction } : null);
    },
    []
  );

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'user',
        label: 'User',
        icon: <PersonIcon style={{ fontSize: 14 }} />,
        variant: 'searchable',
        value: user,
        multiple: true,
        searchableOptions: userFilterOptions,
        searchableLoading: userFilterLoading,
        searchableOnSearch: onUserFilterSearch,
        onChange: (value) => {
          setFilter('user', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: THREAT_HUNTING_SEVERITY_FILTER_OPTIONS,
        value: severityBand,
        multiple: true,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'mitre',
        label: 'MITRE Technique',
        icon: <SecurityIcon style={{ fontSize: 14 }} />,
        options: mapThreatHuntingFacetOptions(facets.mitreIds),
        value: mitreId,
        multiple: true,
        onChange: (value) => {
          setFilter('mitreId', value);
          setPage(0);
        },
      },
      {
        id: 'eventType',
        label: 'Event Type',
        icon: <CategoryIcon style={{ fontSize: 14 }} />,
        options: mapThreatHuntingFacetOptions(facets.eventTypes),
        value: eventType,
        multiple: true,
        onChange: (value) => {
          setFilter('eventType', value);
          setPage(0);
        },
      },
    ],
    [
      user,
      severityBand,
      mitreId,
      eventType,
      userFilterOptions,
      userFilterLoading,
      onUserFilterSearch,
      facets.mitreIds,
      facets.eventTypes,
      setFilter,
    ]
  );

  const columns: Column[] = useMemo(() => getThreatHuntingFindingListColumns('global'), []);
  const affectedEndpointColumns: Column[] = useMemo(
    () => getThreatHuntingAffectedEndpointListColumns(),
    []
  );

  const affectedEndpointsToolbar = (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Typography style={{ fontSize: 13, fontWeight: 700, color: EDR_COLORS.textPrimary }}>
        Affected Endpoints
      </Typography>
      <Typography style={{ fontSize: 12, color: EDR_COLORS.textMuted }}>
        {formatEdrNumber(affectedEndpointsTotal)} endpoints affected
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Threat Hunting')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Threat Hunting"
          subtitle="Search, investigate, and analyze endpoint activity to identify potential threats before they become incidents."
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <EdrGlobalScopeFilters
            pageOrganizationId={organizationId || undefined}
            onChange={() => setPage(0)}
          />
          {canResultsList ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          ) : null}
        </Box>

        {showUnavailable ? (
          <SubscriptionUnavailableMessage messageKey="unavailable.threatHunting" />
        ) : null}

        {canSummary ? <ThreatSummaryKpiGrid summary={summary} /> : null}

        {canAffectedEndpoints ? (
          <EdrDataCard toolbar={affectedEndpointsToolbar}>
            <Table
              columns={affectedEndpointColumns}
              data={affectedEndpoints as unknown as TableData[]}
              loading={loading}
              showPagination={false}
              onSort={handleEndpointsSort}
              resetSortFlag={resetEndpointsSortFlag}
              onRowClick={(row) =>
                openEndpointDetails(row as unknown as WazuhThreatHuntingEndpointItem)
              }
              TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            />
          </EdrDataCard>
        ) : null}

        {canResultsList ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                setPage(0);
              }}
              searchPlaceholder="Search Results…"
              filters={toolbarFilters}
              onReset={() => {
                handleResetFilters();
                setEndpointsSortParams(null);
                setResetEndpointsSortFlag((prev) => !prev);
              }}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            onRowClick={(row) => openFindingDetail(row as unknown as WazuhThreatHuntingFindingItem)}
          />
        </EdrDataCard>
        ) : null}
      </Box>
    </>
  );
};

export default ThreatHunting;
